/* ════════════════════════════════════════════════════════════════════════
   CART DRAWER — Performance Boutique · build per tema HORIZON (Shopify)
   Posiziona in: assets/cart-drawer.js

   Differenze rispetto alla build Expanse:
   - syncTheme() ora dispatcha CartUpdateEvent / CartAddEvent (Horizon)
     invece di `cart:build`. Horizon ascolta `cart:update` per aggiornare
     header count, minicart nativo, ecc.
   - Il fallback quick-add/quick-view ascolta `cart:update` (Horizon) invece
     di `ajaxProduct:added` (Expanse).
   - Gli errori usano CartErrorEvent (`cart:error`) invece di `ajaxProduct:error`.
   - Il click trigger del cart icon usa il selettore Horizon
     `[data-drawer-open="cart-drawer"]` oltre agli attributi generici.
   - DiscountUpdateEvent viene dispatchato dopo applyDiscount/removeDiscount
     per mantenere Horizon allineato.
   - Variant events (`variant:update`, `variant:selected`) sono ascoltati
     per aggiornare lastAddedProductId se il PDP cambia variante.
   - Supporto blocchi ADDON (shipping insurance / upsell checkbox):
     onAddonToggle() + refreshAddonKeys() nel listener click delegato
     e in fondo a applySections().

   API pubblica:  window.PBCartDrawer.open() / .close() / .refresh()
   ════════════════════════════════════════════════════════════════════════ */

/* ─── Import-like shim: se Horizon espone le classi evento come ES module
       non bundlato, le leggiamo da window; altrimenti le definiamo inline
       in forma minimale per non avere dipendenze hard da import.        ─── */
const _CartUpdateEvent = window.CartUpdateEvent ?? class CartUpdateEvent extends Event {
  constructor(resource, sourceId, data) {
    super('cart:update', { bubbles: true });
    this.detail = { resource, sourceId, data: { ...data } };
  }
};
const _CartAddEvent = window.CartAddEvent ?? class CartAddEvent extends Event {
  constructor(resource, sourceId, data) {
    super('cart:update', { bubbles: true });
    this.detail = { resource, sourceId, data: { ...data } };
  }
};
const _CartErrorEvent = window.CartErrorEvent ?? class CartErrorEvent extends Event {
  constructor(sourceId, message, description, errors) {
    super('cart:error', { bubbles: true });
    this.detail = { sourceId, data: { message, errors, description } };
  }
};
const _DiscountUpdateEvent = window.DiscountUpdateEvent ?? class DiscountUpdateEvent extends Event {
  constructor(resource, sourceId) {
    super('discount:update', { bubbles: true });
    this.detail = { resource, sourceId };
  }
};

// ─── Selettori trigger cart icon — Horizon usa data-drawer-open ───────────
const CART_TRIGGER_SELECTORS = [
  '[data-drawer-open="cart-drawer"]',   // Horizon nativo
  '[data-cart-toggle]',                  // attributo generico PB
  'a[href$="/cart"]',
  'a[href*="/cart?"]',
].join(',');

const PB_TAG = 'cart-drawer';

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.panel  = this.querySelector('[data-cd-panel]');
    this.body   = this.querySelector('[data-cd-body]');
    this.recsEl = this.querySelector('[data-cd-recommendations]');
    this.sectionId = this.dataset.sectionId; // = "cart-drawer"
    this.urls = {
      change: this.dataset.cartChangeUrl,
      add:    this.dataset.cartAddUrl,
      recs:   this.dataset.recommendationsUrl,
    };
    this.lastOpener = null;
    this.lastAddedProductId = null;
    this.fetching = false;
    this._skipNextUpdate = false;
    this._justAdded = 0;
  }

  connectedCallback() {
    this.bindEvents();
    window.PBCartDrawer = this;
    document.addEventListener('pb:cart:open', () => this.open());
  }

  /* ─────────────── Eventi UI ─────────────── */
  bindEvents() {
    this.querySelectorAll('[data-cd-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );
    this.querySelector('[data-cd-overlay]')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (!this.classList.contains('is-open')) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') this.handleTab(e);
      if (e.key === 'Enter' && e.target.matches('[data-cd-discount-input]')) {
        e.preventDefault();
        this.applyDiscount(e.target.value.trim());
      }
    });

    this.panel.addEventListener('click', (e) => {
      const step     = e.target.closest('[data-cd-step]');
      const remove   = e.target.closest('[data-cd-remove]');
      const add      = e.target.closest('[data-cd-add]');
      const checkout = e.target.closest('[data-cd-checkout]');
      const prev     = e.target.closest('[data-cd-prev]');
      const next     = e.target.closest('[data-cd-next]');
      const dot      = e.target.closest('[data-cd-dot]');
      const dApply   = e.target.closest('[data-cd-discount-apply]');
      const dRemove  = e.target.closest('[data-cd-discount-remove]');
      const addon    = e.target.closest('[data-cd-addon]');
      if (step)     return this.onStep(step);
      if (remove)   return this.changeLine(remove.dataset.key, 0, remove.closest('[data-cd-item]'));
      if (add)      return this.onUpsellAdd(add);
      if (checkout) return this.onCheckout();
      if (prev)     return this.slideUpsell(-1);
      if (next)     return this.slideUpsell(1);
      if (dot)      return this.slideToUpsell(parseInt(dot.dataset.cdDot, 10));
      if (dApply)   { const i = this.querySelector('[data-cd-discount-input]'); return this.applyDiscount(i ? i.value.trim() : ''); }
      if (dRemove)  return this.removeDiscount();
      if (addon)    return this.onAddonToggle(addon);
    });

    this.panel.addEventListener('change', (e) => {
      const qty = e.target.closest('[data-cd-qty]');
      if (qty) {
        this.changeLine(qty.dataset.key, Math.max(0, parseInt(qty.value, 10) || 0), qty.closest('[data-cd-item]'));
        return;
      }
      const variant = e.target.closest('[data-cd-variant]');
      if (variant) this.onVariantChange(variant);
    });

    /* Horizon: variant:update aggiorna lastAddedProductId se il PDP cambia
       variante (es. combined listing) — utile per le recommendations. */
    document.addEventListener('variant:update', (e) => {
      const pid = e.detail?.data?.productId;
      if (pid) this.lastAddedProductId = pid;
    });
  }

  onStep(btn) {
    const item  = btn.closest('[data-cd-item]');
    const input = item.querySelector('[data-cd-qty]');
    const next  = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(btn.dataset.cdStep, 10));
    this.changeLine(btn.dataset.key, next, item);
  }

  /* ─────────────── Apertura / chiusura ─────────────── */
  open() {
    this.lastOpener = document.activeElement;
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    this.lockScroll();
    this.refreshFromServer();
    this.fetchRecommendations();
    setTimeout(() => this.querySelector('[data-cd-close]')?.focus(), 320);
  }

  close() {
    this.classList.remove('is-open');
    this.unlockScroll();
    const done = () => {
      this.hidden = true;
      this.panel.removeEventListener('transitionend', done);
      if (this.lastOpener?.focus) this.lastOpener.focus();
    };
    this.panel.addEventListener('transitionend', done);
  }

  lockScroll() {
    this.scrollY = window.scrollY;
    document.body.style.cssText = `position:fixed;top:-${this.scrollY}px;left:0;right:0;width:100%;`;
  }
  unlockScroll() {
    document.body.style.cssText = '';
    if (this.scrollY != null) window.scrollTo(0, this.scrollY);
  }

  handleTab(e) {
    const focusable = [...this.panel.querySelectorAll(
      'a[href],button:not([disabled]),input,textarea,[tabindex]:not([tabindex="-1"])'
    )].filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  setLoading(state) { this.classList.toggle('is-loading', state); }

  /* ─────────────── Sincronizzazione ─────────────── */
  async refreshFromServer() {
    try {
      const res  = await fetch(`${window.location.pathname}?sections=${this.sectionId}`);
      const data = await res.json();
      this.applySections(data);
    } catch (err) {
      console.error('[CartDrawer] refresh', err);
    }
  }

  /**
   * syncTheme() — Horizon usa CartUpdateEvent su `cart:update`.
   * Horizon ascolta questo evento per aggiornare il count nell'header
   * e disabilitare/nascondere il drawer nativo.
   *
   * Nota: _skipNextUpdate serve a evitare che il listener globale
   * `cart:update` (vedi fondo file) ri-triggeri un refreshFromServer
   * dopo che siamo già noi a fare il fetch.
   */
  syncTheme(cartData = null) {
    this._skipNextUpdate = true;
    const count = parseInt(this.dataset.cdCount, 10) || 0;
    document.dispatchEvent(
      new _CartUpdateEvent(cartData, 'pb-cart-drawer', {
        itemCount: count,
        source: 'pb-cart-drawer',
      })
    );
  }

  /* ─────────────── Mutazioni ─────────────── */
  async changeLine(key, quantity, itemEl) {
    if (this.fetching) return;
    this.fetching = true;
    this.setLoading(true);
    if (quantity === 0 && itemEl) itemEl.classList.add('is-removing');
    try {
      const res = await fetch(this.urls.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: this.sectionId,
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      this.applySections(data.sections);
      if (parseInt(this.dataset.cdCount, 10) === 0) this.recsEl.innerHTML = '';
      await this.removeOrphanedAddon();
      this.syncTheme(data);
    } catch (err) {
      console.error('[CartDrawer] change', err);
      itemEl?.classList.remove('is-removing');
      // Horizon: notifica l'errore tramite CartErrorEvent
      document.dispatchEvent(new _CartErrorEvent('pb-cart-drawer', err.message, err.message, {}));
    } finally {
      this.fetching = false;
      this.setLoading(false);
    }
  }

  /**
   * removeOrphanedAddon — rimuove l'addon se è rimasto l'unico item nel carrello.
   *
   * Chiamato dopo ogni changeLine (rimozione/modifica quantità prodotto normale).
   * Non viene chiamato in onAddonToggle perché lì gestiamo gli addon direttamente.
   *
   * Legge data-cd-count (già aggiornato da applySections) e confronta con il numero
   * di addon presenti nel DOM. Se coincidono, tutti gli item rimasti sono addon
   * orfani → li rimuove silenziosamente.
   *
   * Richiede le sections solo sull'ultima rimozione per aggiornare il DOM
   * allo stato "carrello vuoto" senza round-trip inutili.
   */
  async removeOrphanedAddon() {
    const totalCount = parseInt(this.dataset.cdCount, 10) || 0;
    if (totalCount === 0) return; // già vuoto, niente da fare

    const addonKeys = [...this.querySelectorAll('[data-cd-addon][data-addon-key]')]
      .map((el) => el.dataset.addonKey)
      .filter(Boolean);

    if (addonKeys.length === 0) return; // nessun addon nel carrello
    if (totalCount > addonKeys.length) return; // ci sono prodotti normali, tutto ok

    // Il carrello contiene solo addon → rimuovili tutti
    for (let i = 0; i < addonKeys.length; i++) {
      const isLast = i === addonKeys.length - 1;
      const res = await fetch(this.urls.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: addonKeys[i],
          quantity: 0,
          // Sections solo sull'ultima chiamata per aggiornare il DOM
          ...(isLast && {
            sections: this.sectionId,
            sections_url: window.location.pathname,
          }),
        }),
      });
      const data = await res.json();
      if (data.status) {
        console.error('[CartDrawer] removeOrphanedAddon', data.description);
        break;
      }
      if (isLast) this.applySections(data.sections);
    }
  }

  async addItems(items) {
    const res = await fetch(this.urls.add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();
    if (data.status) throw new Error(data.description || 'Errore aggiunta');
    this.applySections(data.sections);
    // Horizon: CartAddEvent (stesso nome evento `cart:update`)
    document.dispatchEvent(
      new _CartAddEvent(data, 'pb-cart-drawer', {
        itemCount: parseInt(this.dataset.cdCount, 10) || 0,
        source: 'pb-cart-drawer',
        variantId: String(items[0]?.id ?? ''),
      })
    );
    return data;
  }

  async addFromForm(form, submitter) {
    const fd  = new FormData(form, submitter);
    const btn = submitter || form.querySelector('[type="submit"]');
    btn?.setAttribute('disabled', '');
    this._justAdded = Date.now();
    try {
      const res  = await fetch(this.urls.add, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd,
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || 'Errore');
      this.lastAddedProductId = data.product_id || data.items?.[0]?.product_id || null;
      this.open();
      // Horizon: CartAddEvent dopo apertura drawer
      document.dispatchEvent(
        new _CartAddEvent(data, 'pb-cart-drawer', {
          itemCount: parseInt(this.dataset.cdCount, 10) || 0,
          source: 'pb-cart-drawer',
          productId: String(this.lastAddedProductId ?? ''),
        })
      );
    } catch (err) {
      console.error('[CartDrawer] add', err);
      document.dispatchEvent(new _CartErrorEvent('pb-cart-drawer', err.message, err.message, {}));
    } finally {
      btn?.removeAttribute('disabled');
    }
  }

  /* ─────────────── Addon (Shipping Insurance / checkbox) ─────────────── */

  /**
   * onAddonToggle — toggle add/remove per i blocchi addon.
   *
   * Logica:
   * - Se l'addon cliccato è già nel carrello (data-addon-key valorizzato) → rimuovi.
   * - Se non è nel carrello → prima rimuovi eventuali altri addon già presenti
   *   (tier diverso), poi aggiungi il nuovo. In questo modo non possono
   *   coesistere due addon nello stesso carrello.
   *
   * La ricerca degli addon esistenti avviene su tutti i [data-cd-addon]
   * con data-addon-key non vuoto presenti nel DOM al momento del click
   * (il Liquid li popola dopo ogni applySections).
   *
   * Usa stato ottimistico: aggiorna subito la UI e reverte in caso di errore.
   * Non usa this.fetching per non bloccare le altre interazioni del drawer.
   */
  async onAddonToggle(addonEl) {
    if (addonEl.classList.contains('is-loading')) return;

    const variantId  = parseInt(addonEl.dataset.addonVariantId, 10);
    const cartKey    = addonEl.dataset.addonKey;
    const isInCart   = !!cartKey;

    // Tutti gli addon attualmente nel carrello (incluso eventualmente quello
    // cliccato stesso), raccolti prima di toccare il DOM.
    const existingAddons = [...this.querySelectorAll('[data-cd-addon][data-addon-key]')]
      .filter((el) => el.dataset.addonKey !== '');

    // Stato ottimistico
    addonEl.classList.add('is-loading');
    addonEl.classList.toggle('is-checked', !isInCart);

    try {
      if (isInCart) {
        // ── Remove: l'utente vuole deselezionare l'addon corrente ────
        const res = await fetch(this.urls.change, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: cartKey,
            quantity: 0,
            sections: this.sectionId,
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();
        if (data.status) throw new Error(data.description || 'Errore rimozione addon');
        this.applySections(data.sections);
        this.syncTheme(data);

      } else {
        // ── Replace: rimuovi tutti gli addon esistenti, poi aggiungi ─

        // 1. Rimuovi in sequenza tutti gli addon già nel carrello.
        //    Usiamo /cart/change.js uno per uno; la risposta dell'ultimo
        //    ci dà le sections aggiornate dopo tutte le rimozioni.
        //    (Nella pratica ci sarà al massimo 1 addon da rimuovere.)
        for (const existing of existingAddons) {
          const removeRes = await fetch(this.urls.change, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: existing.dataset.addonKey,
              quantity: 0,
              // Non richiedere sections qui: lo faremo con l'add finale
            }),
          });
          const removeData = await removeRes.json();
          if (removeData.status) throw new Error(removeData.description || 'Errore rimozione addon precedente');
        }

        // 2. Aggiungi il nuovo addon richiedendo le sections aggiornate.
        const addRes = await fetch(this.urls.add, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: this.sectionId,
            sections_url: window.location.pathname,
          }),
        });
        const addData = await addRes.json();
        if (addData.status) throw new Error(addData.description || 'Errore aggiunta addon');
        this.applySections(addData.sections);
        this.syncTheme(addData);
      }

    } catch (err) {
      console.error('[CartDrawer] addon toggle', err);
      // Revert stato ottimistico + notifica Horizon
      addonEl.classList.toggle('is-checked', isInCart);
      document.dispatchEvent(new _CartErrorEvent('pb-cart-drawer', err.message, err.message, {}));
    } finally {
      addonEl.classList.remove('is-loading');
    }
  }

  /**
   * refreshAddonKeys — safety-net dopo applySections().
   * Il footer viene riscritto dall'HTML del server con i data-addon-key
   * già aggiornati dal Liquid, quindi normalmente non serve.
   * Questo metodo è un fallback nel caso gli addon vengano spostati
   * fuori da [data-cd-footer] in futuro.
   */
  refreshAddonKeys() {
    this.querySelectorAll('[data-cd-addon]').forEach((addonEl) => {
      const key = addonEl.dataset.addonKey;
      addonEl.classList.toggle('is-checked', !!key && key !== '');
    });
  }

  /* ─────────────── Upsell carousel ─────────────── */
  initUpsell() {
    const track = this.recsEl.querySelector('[data-cd-track]');
    if (!track) return;
    const dots = [...this.recsEl.querySelectorAll('[data-cd-dot]')];
    const prev = this.recsEl.querySelector('[data-cd-prev]');
    const next = this.recsEl.querySelector('[data-cd-next]');
    const update = () => {
      const i = Math.round(track.scrollLeft / track.clientWidth);
      dots.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
      if (prev) prev.disabled = i <= 0;
      if (next) next.disabled = i >= dots.length - 1;
    };
    track.addEventListener('scroll', () => {
      clearTimeout(this._upsellT);
      this._upsellT = setTimeout(update, 60);
    });
    update();
  }

  slideUpsell(dir) {
    const t = this.recsEl.querySelector('[data-cd-track]');
    if (t) t.scrollBy({ left: dir * t.clientWidth, behavior: 'smooth' });
  }
  slideToUpsell(i) {
    const t = this.recsEl.querySelector('[data-cd-track]');
    if (t) t.scrollTo({ left: i * t.clientWidth, behavior: 'smooth' });
  }

  onVariantChange(select) {
    const slide = select.closest('[data-cd-slide]');
    const opt   = select.selectedOptions[0];
    const price = slide?.querySelector('.pb-cd__slide-price');
    const add   = slide?.querySelector('[data-cd-add]');
    if (price && opt?.dataset.price) price.textContent = opt.dataset.price;
    if (add) add.dataset.variantId = select.value;
  }

  async onUpsellAdd(btn) {
    const slide  = btn.closest('[data-cd-slide]');
    const select = slide?.querySelector('[data-cd-variant]');
    const id     = parseInt(select ? select.value : btn.dataset.variantId, 10);
    const label  = btn.textContent;
    btn.setAttribute('disabled', '');
    try {
      await this.addItems([{ id, quantity: 1 }]);
      btn.classList.add('is-added');
      btn.textContent = 'Aggiunto ✓';
      setTimeout(() => this.fetchRecommendations(), 700);
    } catch (err) {
      console.error('[CartDrawer] upsell add', err);
      btn.textContent = label;
      btn.removeAttribute('disabled');
    }
  }

  async onCheckout() {
    const note = this.querySelector('[data-cd-note]');
    if (note && note.value !== '') {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note.value }),
        });
      } catch (e) { /* prosegui comunque */ }
    }
    window.location.href = '/checkout';
  }

  updateCartCount(n) {
    document.querySelectorAll('[data-cart-count], .cart-link__bubble-num, .cart-count, .site-header__cart-count')
      .forEach((el) => { el.textContent = n; });
    document.querySelectorAll('.cart-link__bubble, [data-cart-count-bubble], .site-header__cart-indicator')
      .forEach((el) => {
        el.classList.toggle('cart-link__bubble--visible', n > 0);
        el.classList.toggle('hide', n === 0);
      });
  }

  /* ─────────────── Discount ─────────────── */
  async applyDiscount(code) {
    if (!code) return;
    this.setLoading(true);
    const msg = this.querySelector('[data-cd-discount-msg]');
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: code,
          sections: this.sectionId,
          sections_url: window.location.pathname,
        }),
      });
      const data  = await res.json();
      const entry = (data.discount_codes || []).find(
        (d) => d.code.toLowerCase() === code.toLowerCase()
      );
      if (entry && entry.applicable) {
        this.applySections(data.sections);
        this.syncTheme(data);
        // Horizon: DiscountUpdateEvent
        document.dispatchEvent(new _DiscountUpdateEvent(data, 'pb-cart-drawer'));
      } else if (msg) {
        msg.hidden = false;
        msg.textContent = entry
          ? 'Codice non applicabile a questi prodotti.'
          : 'Codice non valido.';
      }
    } catch (err) {
      console.error('[CartDrawer] discount', err);
      if (msg) { msg.hidden = false; msg.textContent = 'Errore, riprova.'; }
    } finally {
      this.setLoading(false);
    }
  }

  async removeDiscount() {
    this.setLoading(true);
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: '',
          sections: this.sectionId,
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      this.applySections(data.sections);
      this.syncTheme(data);
      document.dispatchEvent(new _DiscountUpdateEvent(data, 'pb-cart-drawer'));
    } catch (err) {
      console.error('[CartDrawer] discount remove', err);
    } finally {
      this.setLoading(false);
    }
  }

  /* ─────────────── Section Rendering → DOM ─────────────── */
  applySections(sections) {
    if (!sections || !sections[this.sectionId]) return;
    const doc = new DOMParser().parseFromString(sections[this.sectionId], 'text/html');
    const src = doc.querySelector(PB_TAG) || doc;

    ['[data-cd-items]', '[data-cd-footer]'].forEach((sel) => {
      const incoming = src.querySelector(sel);
      const current  = this.querySelector(sel);
      if (current) current.innerHTML = incoming ? incoming.innerHTML : '';
    });

    // Barra con animazione fill preservata
    const barIncoming = src.querySelector('[data-cd-bar]');
    const barCurrent  = this.querySelector('[data-cd-bar]');
    if (barCurrent) {
      const prevFill  = barCurrent.querySelector('.pb-cd__bar-fill');
      const prevWidth = prevFill ? prevFill.style.width : null;
      barCurrent.innerHTML = barIncoming ? barIncoming.innerHTML : '';
      const newFill = barCurrent.querySelector('.pb-cd__bar-fill');
      if (newFill && prevWidth != null) {
        const target = newFill.style.width;
        newFill.style.transition = 'none';
        newFill.style.width = prevWidth;
        void newFill.offsetWidth;
        requestAnimationFrame(() => {
          newFill.style.transition = '';
          newFill.style.width = target;
        });
      }
    }

    const count = src.getAttribute('data-cd-count') || '0';
    this.dataset.cdCount = count;
    const label = this.querySelector('[data-cd-count-label]');
    if (label) label.textContent = count;
    this.classList.toggle('pb-cd--empty', parseInt(count, 10) === 0);
    this.updateCartCount(parseInt(count, 10));

    // ── Aggiorna stato addon dopo swap footer ──────────────────────────
    this.refreshAddonKeys();
  }

  /* ─────────────── Recommendations ─────────────── */
  async fetchRecommendations() {
    if (!this.recsEl) return;
    const firstItem = this.querySelector('[data-cd-item]');
    const productId = this.lastAddedProductId || firstItem?.dataset.productId;
    if (!productId) { this.recsEl.innerHTML = ''; return; }

    const intent = this.dataset.upsellIntent || 'complementary';
    const limit  = this.dataset.upsellLimit || 4;
    const url = `${this.urls.recs}?section_id=cart-recommendations&product_id=${productId}&intent=${intent}&limit=${limit}`;
    try {
      const res   = await fetch(url);
      const doc   = new DOMParser().parseFromString(await res.text(), 'text/html');
      const block = doc.querySelector('[data-cd-upsell]');
      if (block) {
        const heading = this.recsEl.dataset.heading;
        const titleEl = block.querySelector('.pb-cd__upsell-title');
        if (heading && titleEl) titleEl.textContent = heading;
        this.recsEl.innerHTML = block.outerHTML;
        this.initUpsell();
      } else {
        this.recsEl.innerHTML = '';
      }
    } catch (err) {
      console.error('[CartDrawer] recommendations', err);
      this.recsEl.innerHTML = '';
    }
  }

  refresh() { this.refreshFromServer(); this.fetchRecommendations(); }
}

if (!customElements.get(PB_TAG)) customElements.define(PB_TAG, CartDrawer);

/* ════════════════════════════════════════════════════════════════════════
   INTERCETTAZIONE GLOBALE (capture) — precede e blocca Horizon
   ════════════════════════════════════════════════════════════════════════ */

// Add-to-cart: cattura submit prima di Horizon.
document.addEventListener('submit', (e) => {
  const form = e.target.closest('form[action$="/cart/add"], form[action*="/cart/add"]');
  if (!form || form.hasAttribute('data-no-drawer')) return;
  const d = document.querySelector(PB_TAG);
  if (!d || d.dataset.intercept === 'false') return;
  e.preventDefault();
  e.stopImmediatePropagation();
  d.addFromForm(form, e.submitter);
}, true);

// Click su cart icon: apre il drawer PB, blocca Horizon.
// Horizon usa [data-drawer-open="cart-drawer"] — incluso in CART_TRIGGER_SELECTORS.
document.addEventListener('click', (e) => {
  const trigger = e.target.closest(CART_TRIGGER_SELECTORS);
  if (!trigger) return;
  const d = document.querySelector(PB_TAG);
  if (!d || d.dataset.intercept === 'false') return;
  e.preventDefault();
  e.stopImmediatePropagation();
  d.open();
}, true);

/* Horizon quick-add / quick-view: ascolta `cart:update` invece di
   `ajaxProduct:added` (Expanse). Filtra per source per non fare
   un loop con i nostri stessi eventi. */
document.addEventListener('cart:update', (e) => {
  const d = document.querySelector(PB_TAG);
  if (!d || d.dataset.intercept === 'false') return;
  // Evento generato da noi → skip
  if (e.detail?.data?.source === 'pb-cart-drawer') return;
  // Già gestito dalla nostra intercettazione diretta del form → skip
  if (Date.now() - d._justAdded < 1000) return;
  // Quick-add Horizon (prodotto aggiunto da fuori)
  const pid = e.detail?.data?.productId;
  if (pid) d.lastAddedProductId = pid;
  d.open();
});

/* cart:error da Horizon → log (il drawer gestisce già i suoi errori) */
document.addEventListener('cart:error', (e) => {
  if (e.detail?.sourceId === 'pb-cart-drawer') return; // già loggato
  console.warn('[CartDrawer] cart:error da Horizon', e.detail?.data);
});

/* cart:update da sorgenti esterne (non pb-cart-drawer) → rinfresco se aperto */
document.addEventListener('cart:update', (e) => {
  const d = document.querySelector(PB_TAG);
  if (!d) return;
  if (e.detail?.data?.source === 'pb-cart-drawer') return;
  if (d._skipNextUpdate) { d._skipNextUpdate = false; return; }
  if (d.classList.contains('is-open')) d.refreshFromServer();
});

window.openCartDrawer = () => document.querySelector(PB_TAG)?.open();